import React, { useEffect, useState } from 'react';
import { Form, Input, DatePicker, Alert, Button } from 'antd';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import CBModal from './CBModal';
import GedFileField from './GedFileField';
import { partieGarantie } from '../services/partieGarantie';

const { TextArea } = Input;

/**
 * Modal de confirmation de garantie après déclaration.
 *
 * Depuis la séquentialisation des statuts (2026-07-28), cette modale ne fait
 * plus que confirmer la garantie (garantieAcquise = true côté backend, toujours) :
 * le rejet est désormais une action distincte (voir RejeterGarantieModal).
 * Réutilisée telle quelle pour le recours (GARANTIE_REJETEE/SANS_SUITE → GARANTIE_CONFIRMEE),
 * piloté par la prop `mode`.
 *
 * Le backend EXIGE qu'une pièce GED de type CONFIRMATION_GARANTIE soit déjà
 * rattachée au dossier ; ici on gate le bouton OK tant qu'aucun fichier n'est
 * sélectionné, et le parent se charge de l'uploader avant d'appeler l'API de
 * confirmation (cf. onConfirmerGarantie dans DetailsSinistre.js).
 *
 * `onRejeter` (facultatif, mode confirmation uniquement) : ajoute un bouton
 * « Rejeter la garantie » dans le footer pour basculer directement vers le
 * flux de rejet sans repasser par le menu Actions.
 */
export default function ConfirmationGarantieModal({ open, onClose, onConfirm, sinistre, loading, mode = 'confirmation', onRejeter }) {
  const { t } = useTranslation('commun');
  const [form] = Form.useForm();
  const [pieceFile, setPieceFile] = useState(null);

  const recours = mode === 'recours';

  useEffect(() => {
    if (open) {
      form.resetFields();
      form.setFieldsValue({
        dateConfirmationGarantie: dayjs(),
      });
      setPieceFile(null);
    }
  }, [open, form]);

  const handleOk = async () => {
    const values = await form.validateFields();
    onConfirm({
      referenceGarantie: values.referenceGarantie || null,
      dateConfirmationGarantie: values.dateConfirmationGarantie
        ? values.dateConfirmationGarantie.format('YYYY-MM-DD')
        : null,
      observationsGarantie: values.observationsGarantie || null,
      pieceFile,
    });
  };

  return (
    <CBModal
      title={recours ? t('confirmationGarantieModal.titreRecours') : t('confirmationGarantieModal.titre')}
      open={open}
      onCancel={onClose}
      onOk={handleOk}
      okText={recours ? t('confirmationGarantieModal.confirmerRecours') : t('confirmationGarantieModal.confirmerGarantie')}
      cancelText={t('confirmationGarantieModal.annuler')}
      width={560}
      confirmLoading={loading}
      okButtonProps={{
        disabled: !pieceFile,
        style: { background: '#1a6b3c', borderColor: '#1a6b3c' },
      }}
      footer={(_, { OkBtn, CancelBtn }) => (
        <>
          <CancelBtn />
          {!recours && onRejeter && (
            <Button danger disabled={loading} onClick={onRejeter}>
              {t('confirmationGarantieModal.rejeterGarantie')}
            </Button>
          )}
          <OkBtn />
        </>
      )}
    >
      <Alert
        type="info"
        showIcon
        message={t('confirmationGarantieModal.sinistre', { numero: sinistre?.numeroSinistreLocal || '' })}
        description={t('confirmationGarantieModal.confirmationRecue', {
          partie: `${partieGarantie(sinistre).de}${partieGarantie(sinistre).libelle ? ` ${partieGarantie(sinistre).libelle}` : ''}`,
          assureur: sinistre?.organismeMembreRaisonSociale || '-',
          pays: sinistre?.paysEmetteurLibelle || '-',
        })}
        style={{ marginBottom: 16 }}
      />

      <Form form={form} layout="vertical">
        <Form.Item
          name="dateConfirmationGarantie"
          label={t('confirmationGarantieModal.dateConfirmation')}
          rules={[{ required: true, message: t('confirmationGarantieModal.dateObligatoire') }]}
        >
          <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} />
        </Form.Item>

        <Form.Item name="referenceGarantie" label={t('confirmationGarantieModal.reference')}>
          <Input placeholder={t('confirmationGarantieModal.referencePlaceholder')} />
        </Form.Item>

        <Form.Item name="observationsGarantie" label={t('confirmationGarantieModal.observations')}>
          <TextArea rows={3} placeholder={t('confirmationGarantieModal.observationsPlaceholder')} />
        </Form.Item>

        <Form.Item
          label={t('confirmationGarantieModal.piece', { partie: partieGarantie(sinistre).de })}
          required
          extra={t('confirmationGarantieModal.pieceObligatoire')}
        >
          <GedFileField value={pieceFile} onChange={setPieceFile} required hint="type CONFIRMATION_GARANTIE" />
        </Form.Item>
      </Form>
    </CBModal>
  );
}
